import React from 'react';
import { X } from 'lucide-react';

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black"></div>
      {label && <p className="mt-4 text-gray-500 text-sm">{label}</p>}
    </div>
  );
}

export function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-16 text-gray-400">
      <p>{message}</p>
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  maxWidth = 'max-w-2xl',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: string;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className={`bg-white rounded-lg shadow-xl w-full ${maxWidth} max-h-[92vh] flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">{children}</div>
      </div>
    </div>
  );
}

export function StatCard({
  icon,
  label,
  value,
  color = 'text-primary',
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  color?: string;
  sub?: string;
}) {
  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold truncate">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className={color}>{icon}</div>
      </div>
    </div>
  );
}

export const inputClass =
  'w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm';
export const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
export const primaryBtn =
  'px-4 py-2 bg-primary text-black rounded-lg flex items-center space-x-2 hover:bg-primary-dark disabled:opacity-50 text-sm';
export const secondaryBtn =
  'px-4 py-2 border border-gray-300 rounded-lg flex items-center space-x-2 hover:bg-gray-50 text-sm';
export const dangerBtn =
  'px-4 py-2 border border-red-600 text-red-600 rounded-lg flex items-center space-x-2 hover:bg-red-50 text-sm';

export function Avatar({ src, name, className = 'w-10 h-10' }: { src?: string | null; name?: string; className?: string }) {
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  if (src) {
    return <img src={src} alt={name || 'avatar'} className={`rounded-full object-cover ${className}`} />;
  }
  return (
    <div className={`rounded-full bg-primary text-black flex items-center justify-center font-bold overflow-hidden ${className}`}>
      {initials}
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-primary/15 text-primary-dark',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-600',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-700',
    scheduled: 'bg-primary/15 text-primary-dark',
    live: 'bg-green-100 text-green-800',
    sold: 'bg-red-100 text-red-700',
    available: 'bg-green-100 text-green-800',
    open: 'bg-yellow-100 text-yellow-800',
    in_review: 'bg-primary/15 text-primary-dark',
    resolved: 'bg-green-100 text-green-800',
    dismissed: 'bg-gray-100 text-gray-600',
    daily: 'bg-primary/15 text-primary-dark',
    weekly: 'bg-primary/15 text-primary-dark',
    monthly: 'bg-purple-100 text-purple-800',
    diesel: 'bg-amber-100 text-amber-800',
    ekedc: 'bg-orange-100 text-orange-800',
    security_breach: 'bg-red-100 text-red-800',
    incident: 'bg-red-100 text-red-800',
    residents_complaint: 'bg-violet-100 text-violet-800',
    monthly_kpi: 'bg-indigo-100 text-indigo-800',
    electrical: 'bg-amber-100 text-amber-800',
    plumbing: 'bg-primary/15 text-primary-dark',
    cleaning: 'bg-teal-100 text-teal-800',
    pool: 'bg-primary/15 text-primary-dark',
    lift: 'bg-primary/15 text-primary-dark',
    generator: 'bg-lime-100 text-lime-800',
    staff_report: 'bg-primary/15 text-primary-dark',
    residents_report: 'bg-violet-100 text-violet-800',
    maintenance: 'bg-yellow-100 text-yellow-800',
    security: 'bg-rose-100 text-rose-800',
    waste_disposal: 'bg-stone-100 text-stone-800',
    fumigation: 'bg-emerald-100 text-emerald-800',
    water_treatment: 'bg-primary/15 text-primary-dark',
    sewage_maintenance: 'bg-slate-100 text-slate-800',
    road_drainage: 'bg-orange-100 text-orange-800',
    common_area: 'bg-teal-100 text-teal-800',
    status_report: 'bg-primary/15 text-primary-dark',
    request: 'bg-fuchsia-100 text-fuchsia-800',
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${cls}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Delete',
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-bold mb-2">{title}</h2>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className={secondaryBtn}>Cancel</button>
          <button onClick={() => { onConfirm(); onClose(); }} className={dangerBtn}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export function Alert({ type = 'error', children }: { type?: 'error' | 'success' | 'info'; children: React.ReactNode }) {
  const styles = {
    error: 'bg-red-50 text-red-700 border-red-200',
    success: 'bg-green-50 text-green-700 border-green-200',
    info: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return <div className={`${styles[type]} border px-4 py-3 rounded-lg text-sm mb-4`}>{children}</div>;
}

/**
 * Progress bar for a file upload.
 *
 * Shows three distinct states, because "100%" of the request body being sent is
 * not the same as the upload having succeeded:
 *   - uploading  — bytes are still going over the connection
 *   - processing — the server has everything and is writing files / rows
 *   - done       — confirmed by the server
 *
 * `error` replaces the bar with the server's own message, so a failure never
 * looks like a silent no-op.
 */
export function UploadProgress({
  percent,
  phase,
  error,
  onCancel,
}: {
  percent: number;
  phase: 'idle' | 'uploading' | 'processing' | 'done' | 'error';
  error?: string;
  onCancel?: () => void;
}) {
  if (phase === 'idle') return null;

  if (phase === 'error') {
    return (
      <div className="mt-3">
        <Alert>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold">Upload failed</p>
              <p className="mt-1 break-words">{error || 'The upload did not complete. Please try again.'}</p>
            </div>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="shrink-0 text-xs font-medium underline hover:no-underline"
              >
                Dismiss
              </button>
            )}
          </div>
        </Alert>
      </div>
    );
  }

  const done = phase === 'done';
  const processing = phase === 'processing';

  return (
    <div className="mt-3" aria-live="polite">
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="font-medium text-gray-700">
          {done ? 'Upload complete' : processing ? 'Processing files on the server...' : 'Uploading files...'}
        </span>
        <span className="text-gray-500 tabular-nums">{done ? 100 : percent}%</span>
      </div>
      <div
        className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden"
        role="progressbar"
        aria-valuenow={done ? 100 : percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-2.5 rounded-full transition-all duration-200 ${
            done ? 'bg-green-500' : processing ? 'bg-blue-500 animate-pulse' : 'bg-primary'
          }`}
          style={{ width: `${done ? 100 : Math.max(percent, processing ? 99 : 2)}%` }}
        />
      </div>
      {onCancel && !done && (
        <button
          type="button"
          onClick={onCancel}
          className="mt-1.5 text-xs text-gray-500 hover:text-red-600 underline"
        >
          Cancel upload
        </button>
      )}
    </div>
  );
}
